<template>
  <div class="ai-chat" :class="{ 'ai-chat__window-mode': windowMode }">
    <header class="ai-chat__header">
      <button
        type="button"
        class="size-24 cursor-pointer"
        @click="emit('ai-chat-closed')"
      >
        <FontAwesomeIcon
          :icon="['fak', 'x']"
          class="text-neutral-accent h-20 w-20"
        />
      </button>
    </header>
    <div class="ai-chat__body">
      <div
        v-if="
          !receivedMessagesInOrder?.length &&
          !nextMessage &&
          !isChatResponding &&
          !connectionFailed &&
          !connecting
        "
        class="ai-chat__suggested-prompts"
      >
        <FontAwesomeIcon
          :icon="['far', 'sparkles']"
          class="text-beta-text mb-20"
          style="width: 30px; height: 30px"
        />
        <div class="flex items-center gap-8 mb-20">
          <span class="ai-chat__title ai-chat__gradient"
            >Crossbeam AI Chat</span
          >
          <BittsTag color="neutral" size="x-small" text="Beta" />
        </div>
        <span class="text-neutral-text-weak mb-8">Suggested prompts:</span>
        <div class="flex flex-col items-center gap-16 max-w-full">
          <button
            v-for="prompt in suggestedPrompts"
            :key="prompt"
            type="button"
            class="ai-chat__suggested-prompt"
            @click.prevent="() => sendSuggestedPrompt(prompt)"
          >
            {{ prompt }}
          </button>
        </div>
      </div>
      <div v-else class="ai-chat__messages">
        <div
          v-for="(message, index) in receivedMessagesInOrder"
          :key="index"
          :data-testid="`chat-message-${message.role}`"
          :class="{
            'ai-chat__message': !message.errorCode,
            [message.role]: !message.errorCode,
            'opacity-40': messageCausedError(index),
          }"
        >
          <div v-if="!message.errorCode">
            <div class="flex items-center gap-8">
              <FontAwesomeIcon
                v-if="message.role === 'user' && messageCausedError(index)"
                data-testid="message-error-icon"
                :icon="['fak', 'warning']"
                class="text-secondary-text"
                style="width: 16px; height: 16px"
              />
              <div v-html="message.content" class="ai-chat__html" />
            </div>
            <FeedbackControls
              v-if="message.role === 'bot'"
              :message="message"
              :base-itly-body="baseItlyBody"
              :chat-session-id="chatSessionId"
              :messages-with-feedback="messagesWithFeedback"
              @feedback-added="
                (feedback: MessageFeedback) =>
                  messagesWithFeedback.push(feedback)
              "
            />
          </div>
        </div>
        <div v-if="nextMessage" class="bot ai-chat__message">
          <div v-html="formattedNextMessage" class="ai-chat__html" />
        </div>
        <div v-if="connecting" data-testid="connecting" class="flex gap-5">
          <FontAwesomeIcon
            :icon="['fak', 'loading']"
            class="animate-spin text-neutral-text-placeholder"
            style="width: 20px; height: 20px"
          />
          <b>Connecting...</b>
        </div>
        <BittsCallout
          v-if="errorInfo"
          :subtitle="errorInfo?.message"
          type="warning"
          class="mb-10"
          size="x-small"
        >
          <template #bottom-item>
            <div class="flex justify-between items-center">
              <BittsButton
                :left-icon="['fak', inTimeout ? 'loading' : 'refresh']"
                :disabled="inTimeout"
                :text="inTimeout ? errorInfo?.timeoutText : errorInfo?.action"
                type="primary"
                size="x-small"
                class="self-start mt-12"
                :class="{
                  'retry-button-timeout': inTimeout,
                }"
                @click="errorInfo?.onClick"
              />
            </div>
          </template>
        </BittsCallout>
        <div
          v-if="isChatResponding && !hasReceivedFirstResponse && !connecting"
          class="flex items-center gap-5"
        >
          <FontAwesomeIcon
            :icon="['fak', 'loading']"
            class="animate-spin text-neutral-text-placeholder"
            style="width: 20px; height: 20px"
          />
          <b>Working...</b>
        </div>
      </div>
    </div>
    <footer class="ai-chat__footer">
      <BittsLink
        text="Switch chat mode"
        :show-arrow="true"
        @click.stop="changeMode"
      />
      <div class="flex items-center gap-8">
        <BittsSwitch v-model="sendContext" />
        <b class="text-neutral-text">Include page context</b>
        <BittsTooltip trigger="hover" placement="right">
          <FontAwesomeIcon
            :icon="['fak', 'question-circle']"
            class="text-neutral-text-placeholder"
            style="width: 16px; height: 16px"
          />
          <template #title>
            Share page context with Crossbeam AI to improve the relevance of its
            answer.
          </template>
        </BittsTooltip>
      </div>
      <BittsInput
        ref="chatInput"
        data-testid="ai-chat-input"
        v-model="messageToSend"
        :disabled="sendDisabled"
        name="AI Chat Input"
        :focus="visible"
        :placeholder="
          waitingOnResponse
            ? 'Generating response...'
            : 'Type your message here...'
        "
        @keyup.enter="handleEnter"
        class="w-full mt-auto"
        rows="3"
      >
        <template #suffix>
          <BittsButton
            data-testid="ai-chat-send-button"
            :disabled="sendDisabled || waitingOnResponse"
            type="primary"
            class="ai-chat__send"
            size="medium"
            :center-icon="
              isChatResponding && !waitingOnResponse
                ? ['fas', 'stop']
                : ['fak', 'arrow-up']
            "
            :aria-label="
              isChatResponding && !waitingOnResponse
                ? 'Stop response'
                : 'Send message'
            "
            @click.prevent="onSendOrCancel"
          />
        </template>
      </BittsInput>
    </footer>
  </div>
</template>
<script setup lang="ts">
import {
  BittsButton,
  BittsCallout,
  BittsInput,
  BittsLink,
  BittsSwitch,
  BittsTag,
  BittsTooltip,
} from '@crossbeam/bitts';
import { Timeout } from '@crossbeam/types';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { computed, inject, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

import { MessageFeedback } from '@/composables/mcp/useAIChatState';
import { useMessageBuilder } from '@/composables/mcp/useMessageBuilder';
import appConfig from '@/config';
import { AI_CHAT_MODES } from '@/constants/mcp';
import { ProvidedAIChatMode } from '@/injectionKeys/global';
import { intercomOrEmailMessage } from '@/utils';

import FeedbackControls from './FeedbackControls.vue';

const { visible = false } = defineProps<{
  visible?: boolean;
}>();

const emit = defineEmits<(e: 'ai-chat-closed') => void>();

const windowMode = computed(() => {
  return aiChatMode.value === AI_CHAT_MODES.WINDOW;
});

const {
  chatSessionId,
  receivedMessagesInOrder,
  formattedNextMessage,
  isChatResponding,
  hasReceivedFirstResponse,
  messageToSend,
  nextMessage,
  suggestedPrompts,
  sendMessage,
  sendContext,
  sendSuggestedPrompt,
  wsStore,
  baseItlyBody,
  iteratively,
  errorInfo,
  inTimeout,
  messagesWithFeedback,
  cancelMessage,
  waitingOnResponse,
  isConnected,
  connectionFailed,
  sendLastUserMessage,
} = useMessageBuilder();

const aiChatMode = inject(ProvidedAIChatMode) || ref(AI_CHAT_MODES.DRAWER);

function openSupportChat() {
  const lastUserMessage = receivedMessagesInOrder.value
    .toReversed()
    .find((msg) => msg.role === 'user')?.content;
  const body = `I need help with:\n${lastUserMessage ?? ''}`;
  intercomOrEmailMessage('support@crossbeam.com', 'Crossbeam Support', body);
}

function changeMode() {
  if (aiChatMode.value === AI_CHAT_MODES.DRAWER) {
    aiChatMode.value = AI_CHAT_MODES.WINDOW;
  } else {
    aiChatMode.value = AI_CHAT_MODES.DRAWER;
  }
}

watch(
  () => visible,
  (isVisible) => {
    if (isVisible) wsStore?.connect();
  },
  { immediate: true },
);

const connecting = computed(() => {
  return !isConnected?.value && !connectionFailed?.value;
});

const sendLastUserMessageOnConnect = ref(false);
watch(
  () => connecting.value,
  (newVal) => {
    if (newVal) {
      scrollToBottom();
      nextMessage.value = '';
      const messageLength = receivedMessagesInOrder?.value?.length || 0;
      const wasLastFullMessageUserMessage =
        messageLength &&
        receivedMessagesInOrder.value[messageLength - 1]?.role === 'user';
      if (wasLastFullMessageUserMessage) {
        waitingOnResponse.value = false;
        isChatResponding.value = false;
        sendLastUserMessageOnConnect.value = true;
      }
    } else if (sendLastUserMessageOnConnect.value && !connectionFailed?.value) {
      sendLastUserMessage(false);
      sendLastUserMessageOnConnect.value = false;
    }
  },
);

function scrollToBottom() {
  nextTick(() => {
    const query = windowMode.value ? '.ai-chat__messages' : '.ant-drawer-body';
    const drawerBody = document.querySelector(query);
    if (drawerBody) {
      drawerBody.scrollTop = drawerBody.scrollHeight;
    }
  });
}

watch(
  () => nextMessage?.value,
  (newVal) => {
    if (newVal) {
      nextTick(() => {
        scrollToBottom();
      });
    }
  },
);

const chatInput = ref<InstanceType<typeof BittsInput> | null>(null);

function messageCausedError(idx: number) {
  if (!receivedMessagesInOrder?.value?.length) return false;
  return receivedMessagesInOrder?.value[idx + 1]?.errorCode;
}

let initializeDrawerTimer: Timeout | undefined;
function initializeDrawer() {
  clearTimeout(initializeDrawerTimer);
  initializeDrawerTimer = setTimeout(() => {
    if (visible) {
      scrollToBottom();
      chatInput.value?.$el.querySelector('input')?.focus();
    }
  }, 100);
}

watch(
  () => visible,
  (newVal) => {
    if (newVal) initializeDrawer();
  },
  { immediate: true },
);

const router = useRouter();
const ALLOWED_DOMAINS = [
  'app.crossbeam.com',
  'app.crossbeam-dev.com',
  'app.cb.c7m.io',
  'help.crossbeam.com',
  'insider.crossbeam.com',
  'reveal.co',
  'www.crossbeam.com',
  'crossbeam.com',
];

function isLinkAllowed(link: HTMLAnchorElement): boolean {
  const href = link.getAttribute('href');
  if (!href) return false;
  const url = new URL(href);
  return ALLOWED_DOMAINS.some((domain) => url.hostname === domain);
}

function handleContextMenu(event: MouseEvent) {
  const target = event.target as HTMLElement;
  const link = target.closest('a');

  if (link && !isLinkAllowed(link)) {
    event.preventDefault();
  }
}

async function handleLinkClick(event: MouseEvent) {
  const target = event.target as HTMLElement;
  const link = target.closest('a');

  if (link) {
    const href = link.getAttribute('href');
    if (!href) return;
    if (href.startsWith('mailto:')) {
      return;
    }
    if (href.includes('/open-support-chat')) {
      event.preventDefault();
      openSupportChat();
      return;
    }
    event.preventDefault();
    const url = new URL(href);
    const isValidLink = isLinkAllowed(link);
    if (isValidLink) {
      const isInternalRoute = href.includes(appConfig.host);
      if (isInternalRoute) {
        await router.replace(
          `${url.pathname}${url.search ? `${url.search}&from_ai_chat=true` : '?from_ai_chat=true'}`,
        );
        emit('ai-chat-closed');
      } else {
        window.open(href, '_blank');
      }
      iteratively.aiChatAction({
        ...baseItlyBody.value,
        type: 'link_click',
        metadata: {
          link: href,
        },
      });
    }
  }
}

const sendDisabled = computed(() => {
  return inTimeout.value || connecting.value || connectionFailed?.value;
});

function onSendOrCancel() {
  if (!isChatResponding.value) onSend();
  else cancelMessage();
}
function onSend() {
  sendMessage();
  nextTick(() => {
    scrollToBottom();
  });
}

function handleEnter(event: KeyboardEvent) {
  if (!isChatResponding.value && !sendDisabled.value) {
    onSend();
  } else {
    event.preventDefault();
  }
}

watch(
  () => receivedMessagesInOrder?.value.length,
  (numMessages) => {
    if (numMessages) {
      nextTick(() => {
        scrollToBottom();
      });
    }
  },
);
watch(
  [
    () => receivedMessagesInOrder?.value?.map((m) => m.content),
    () => formattedNextMessage?.value,
  ],
  ([messages, formatted]) => {
    if (messages?.length || formatted) {
      nextTick(() => {
        const htmlContainers: HTMLElement[] = Array.from(
          document.querySelectorAll('.ai-chat__html'),
        );
        htmlContainers.forEach((container) => {
          container.removeEventListener('click', handleLinkClick);
          container.removeEventListener('contextmenu', handleContextMenu);
          container.addEventListener('click', handleLinkClick);
          container.addEventListener('contextmenu', handleContextMenu);
        });
      });
    }
  },
  { deep: true },
);

onBeforeUnmount(() => {
  clearTimeout(initializeDrawerTimer);
  const htmlContainers: HTMLElement[] = Array.from(
    document.querySelectorAll('.ai-chat__html'),
  );
  htmlContainers.forEach((container) => {
    container.removeEventListener('click', handleLinkClick);
    container.removeEventListener('contextmenu', handleContextMenu);
  });
});

defineExpose({
  waitingOnResponse,
  aiChatMode,
});
</script>

<style lang="pcss" scoped>
@reference "../../css/theme.css";
.ai-chat {
  @apply bg-neutral-background-weak rounded-bts-lg;
}
.ai-chat__body {
  @apply mt-[33px];
}
.ai-chat__header {
  @apply z-40 w-full absolute top-0 flex items-center justify-end pt-6 pr-4 pb-2 border-b border-solid border-neutral-border rounded-t-bts-lg bg-neutral-background-weak;
}
.ai-chat__footer {
  @apply w-full flex flex-col gap-16 bg-white border-t border-solid border-neutral-border p-16 rounded-b-bts-lg;
}
.retry-button-timeout {
  :deep(.c-bitts-btn__left-icon) {
    @apply animate-spin;
    transform-origin: center center;
    transform-box: fill-box;
  }
}
:deep(.bitts-callout) {
  svg.fa-information-circle {
    @apply hidden;
  }
}
.ai-chat__html {
  @apply flex flex-col gap-16 min-w-0;
  :deep(a) {
    @apply text-secondary-text font-bold;
    &:hover {
      @apply text-secondary-border;
    }
  }
  :deep(strong) {
    @apply font-bold text-neutral-text-strong;
  }
  :deep(hr) {
    @apply border-neutral-border mx-24;
  }
  :deep(h1) {
    @apply text-m font-bold;
  }
  :deep(h2) {
    @apply font-bold;
  }
  :deep(h3) {
    @apply font-bold;
  }
  :deep(ol) {
    @apply list-decimal pl-16;
  }
  :deep(ul) {
    @apply list-disc pl-16;
  }
  :deep(table) {
    @apply block max-w-full overflow-x-auto border-collapse text-left;
  }
  :deep(th),
  :deep(td) {
    @apply border-b border-neutral-border px-8 py-4 align-top text-neutral-text wrap-normal;
  }
  :deep(th) {
    @apply font-bold text-neutral-text-strong;
  }
  :deep(p),
  :deep(li),
  :deep(pre) {
    @apply max-w-full wrap-break-word whitespace-normal;
  }

  :deep(p),
  :deep(li) {
    @apply text-neutral-text my-4;
  }
}
.ai-chat__messages {
  @apply flex flex-col h-full p-16 w-full overflow-y-auto;
}
.ai-chat__suggested-prompts {
  @apply flex flex-col items-center justify-center h-full py-32;
}
.ai-chat__suggested-prompt {
  @apply cursor-pointer max-w-full text-wrap mx-48 bg-white rounded-bts-sm px-10 py-4 text-base text-neutral-text text-center;
}
.ai-chat__message {
  @apply mb-24 py-4 px-12 rounded-8 w-auto max-w-full wrap-break-word whitespace-normal;
  &.user {
    @apply self-end text-right bg-secondary-background-weak;
  }
  &.bot {
    @apply bg-neutral-background;
  }
}
.ai-chat__window-mode {
  @apply border border-solid border-neutral-border;
  .ai-chat__body {
    @apply h-[440px];
  }
  .ai-chat__header {
    @apply rounded-t-bts-md max-w-full border border-solid border-neutral-border left-0;
  }
}
.ai-chat__send.c-bitts-btn--fill:not(
    .active
  ).c-bitts-btn-primary.c-bitts-btn--fill {
  @apply rounded-bts-xxl;
  background: linear-gradient(
    95deg,
    #1c2082 3.05%,
    rgba(94, 29, 131, 0.75) 68.57%,
    rgba(135, 22, 107, 0.75) 95.34%
  );
}
.ai-chat__title {
  @apply text-xl font-bold;
}

.ai-chat__gradient {
  background: var(
    --AI-Gradient,
    linear-gradient(
      95deg,
      #1c2082 3.05%,
      rgba(94, 29, 131, 0.75) 68.57%,
      rgba(135, 22, 107, 0.75) 95.34%
    )
  );
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

@keyframes spin-in-place {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.spin-in-place {
  animation: spin-in-place 1s linear infinite;
  transform-origin: center center;
  display: inline-block;
}
</style>
